import { ART_PALETTE } from '../art-palette.js';
import { repairAppearance, repairName } from './appearance.js';
import {
  BOTTOMS,
  CLOTH_COLORS,
  DIRECTIONS,
  FRAME_HEIGHT,
  FRAME_WIDTH,
  GENDERS,
  HAIR_COLORS,
  HAIR_STYLES,
  SKIN_TONES,
  TOPS,
  optionSwatch,
  walkFrame,
} from './catalog.js';
import { composeAppearance, sheetToCanvas } from './compose.js';
import { NPC_BEHAVIORS, WANDER_RADII } from '../npc/catalog.js';

const PREVIEW_SCALE = 8;

const NPC_MODES = new Set(['npc', 'npc-edit']);

const TITLES = {
  edit: 'Edit character',
  npc: 'Create NPC',
  'npc-edit': 'Edit NPC',
};

function swatchColor(index) {
  return ART_PALETTE[index].slice(0, 7);
}

function fieldset(title, name, inner) {
  const group = document.createElement('fieldset');
  const legend = document.createElement('legend');
  legend.textContent = title;
  const row = document.createElement('div');
  row.className = 'character-options';
  row.append(inner);
  group.append(legend, row);
  group.dataset.field = name;
  return group;
}

function optionButton(name, value, label, { swatch, pressed } = {}) {
  const button = document.createElement('button');
  button.type = 'button';
  button.name = name;
  button.value = value;
  button.setAttribute('aria-pressed', pressed ? 'true' : 'false');
  button.className = swatch ? 'character-option character-option--swatch' : 'character-option';
  if (swatch) {
    button.style.setProperty('--swatch', swatchColor(swatch));
    button.title = label;
    button.setAttribute('aria-label', label);
  } else {
    button.textContent = label;
  }
  return button;
}

export class CharacterCustomizer {
  constructor({ root, onConfirm, onCancel, onDelete, validateName }) {
    this.root = root;
    this.onConfirm = onConfirm;
    this.onCancel = onCancel;
    this.onDelete = onDelete;
    this.validateName = validateName;
    this.mode = 'edit';
    this.draft = repairAppearance();
    this.npcDraft = { id: 0, behavior: 'still', wanderRadius: 'medium' };
    this.direction = 'down';
    this.walkTime = 0;
    this.raf = 0;
    this.sheetCanvas = null;
    this.preview = root.querySelector('#character-preview');
    this.previewCtx = this.preview.getContext('2d');
    this.previewCtx.imageSmoothingEnabled = false;
    this.title = root.querySelector('#character-customizer-title');
    this.form = root.querySelector('#character-form');
    this.nameInput = root.querySelector('#character-name');
    this.groups = root.querySelector('#character-groups');
    this.npcGroups = root.querySelector('#npc-groups');
    this.nameError = root.querySelector('#character-name-error');
    this.actions = root.querySelector('#character-actions');
    this.confirmButton = root.querySelector('#character-confirm');
    this.cancelButton = root.querySelector('#character-cancel');
    this.deleteButton = root.querySelector('#character-delete');
    this.deleteStrip = root.querySelector('#character-delete-confirm');
    this.deletePrompt = root.querySelector('#character-delete-prompt');
    this._renderControls();
    this._renderNpcControls();
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this._confirm();
    });
    this.cancelButton.addEventListener('click', () => this._cancel());
    this.nameInput.addEventListener('input', () => {
      this.draft.name = repairName(this.nameInput.value);
      this._syncConfirm();
    });
    this.npcGroups.addEventListener('click', (event) => {
      const button = event.target.closest('button[name]');
      if (!button) return;
      this.npcDraft = { ...this.npcDraft, [button.name]: button.value };
      this._syncNpcButtons();
    });
    this.deleteButton.addEventListener('click', () => this._askDelete());
    this.deleteStrip.querySelector('#character-delete-cancel')
      .addEventListener('click', () => this._closeDeletePrompt());
    this.deleteStrip.querySelector('#character-delete-yes')
      .addEventListener('click', () => this._deleteNpc());
    this.groups.addEventListener('click', (event) => {
      const button = event.target.closest('button[name]');
      if (!button) return;
      this.draft = repairAppearance({ ...this.draft, [button.name]: button.value });
      this._syncButtons();
      this._recompose();
    });
    this.preview.addEventListener('click', () => this._turn(1));
    this._onKey = (event) => this._handleKey(event);
  }

  get open() {
    return !this.root.hidden;
  }

  show(appearance, mode = 'edit', extra = {}) {
    this.mode = mode;
    this.draft = repairAppearance(appearance);
    this.npcDraft = {
      id: extra.id ?? 0,
      behavior: extra.behavior ?? 'still',
      wanderRadius: extra.wanderRadius ?? 'medium',
    };
    this.direction = 'down';
    this.walkTime = 0;
    this.title.textContent = TITLES[mode] ?? 'Create your character';
    // A fresh NPC starts with an empty, required name rather than the
    // repairAppearance fallback of 'Player'.
    this.nameInput.value = mode === 'npc' ? '' : this.draft.name;
    this._closeDeletePrompt();
    this.npcGroups.hidden = !NPC_MODES.has(mode);
    this.deleteButton.hidden = mode !== 'npc-edit';
    this._syncNpcButtons();
    this._syncButtons();
    this._syncConfirm();
    this._recompose();
    this.root.hidden = false;
    this.nameInput.focus();
    this.nameInput.select();
    window.addEventListener('keydown', this._onKey);
    this._tick(performance.now());
  }

  hide() {
    this.root.hidden = true;
    window.removeEventListener('keydown', this._onKey);
    cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  _renderControls() {
    const groups = [
      fieldset('Gender', 'gender', GENDERS.reduce((frag, option) => {
        frag.append(optionButton('gender', option.id, option.name));
        return frag;
      }, document.createDocumentFragment())),
      fieldset('Skin', 'skin', SKIN_TONES.reduce((frag, option) => {
        frag.append(optionButton('skin', option.id, option.name, { swatch: optionSwatch(option) }));
        return frag;
      }, document.createDocumentFragment())),
      fieldset('Hair', 'hairStyle', HAIR_STYLES.reduce((frag, option) => {
        frag.append(optionButton('hairStyle', option.id, option.name));
        return frag;
      }, document.createDocumentFragment())),
      fieldset('Hair color', 'hairColor', HAIR_COLORS.reduce((frag, option) => {
        frag.append(optionButton('hairColor', option.id, option.name, { swatch: optionSwatch(option) }));
        return frag;
      }, document.createDocumentFragment())),
      fieldset('Top', 'top', TOPS.reduce((frag, option) => {
        frag.append(optionButton('top', option.id, option.name));
        return frag;
      }, document.createDocumentFragment())),
      fieldset('Top color', 'topColor', CLOTH_COLORS.reduce((frag, option) => {
        frag.append(optionButton('topColor', option.id, option.name, { swatch: optionSwatch(option) }));
        return frag;
      }, document.createDocumentFragment())),
      fieldset('Bottom', 'bottom', BOTTOMS.reduce((frag, option) => {
        frag.append(optionButton('bottom', option.id, option.name));
        return frag;
      }, document.createDocumentFragment())),
      fieldset('Bottom color', 'bottomColor', CLOTH_COLORS.reduce((frag, option) => {
        frag.append(optionButton('bottomColor', option.id, option.name, { swatch: optionSwatch(option) }));
        return frag;
      }, document.createDocumentFragment())),
    ];
    this.groups.replaceChildren(...groups);
  }

  _renderNpcControls() {
    const behavior = fieldset('Behavior', 'behavior', NPC_BEHAVIORS.reduce((frag, option) => {
      frag.append(optionButton('behavior', option.id, option.name));
      return frag;
    }, document.createDocumentFragment()));
    const radius = fieldset('Wander radius', 'wanderRadius', WANDER_RADII.reduce((frag, option) => {
      frag.append(optionButton('wanderRadius', option.id, `${option.name} (${option.tiles})`));
      return frag;
    }, document.createDocumentFragment()));
    this.radiusGroup = radius;
    this.npcGroups.replaceChildren(behavior, radius);
  }

  _syncNpcButtons() {
    for (const button of this.npcGroups.querySelectorAll('button[name]')) {
      button.setAttribute('aria-pressed', String(button.value === this.npcDraft[button.name]));
    }
    // A still NPC has no leash to size.
    this.radiusGroup.hidden = this.npcDraft.behavior !== 'wander';
  }

  _askDelete() {
    this.deletePrompt.textContent
      = `Delete ${repairName(this.nameInput.value)}? They are removed from every world.`;
    this.actions.hidden = true;
    this.deleteStrip.hidden = false;
    this.deleteStrip.querySelector('#character-delete-cancel').focus();
  }

  _closeDeletePrompt() {
    this.actions.hidden = false;
    this.deleteStrip.hidden = true;
  }

  _deleteNpc() {
    const { id } = this.npcDraft;
    this.hide();
    this.onDelete?.(id);
  }

  _syncButtons() {
    for (const button of this.groups.querySelectorAll('button[name]')) {
      button.setAttribute('aria-pressed', button.value === this.draft[button.name] ? 'true' : 'false');
    }
  }

  _syncConfirm() {
    const name = this.nameInput.value.trim();
    const error = name ? (this.validateName?.(name, this.mode, this.npcDraft.id) ?? '') : '';
    this.nameError.textContent = error;
    this.confirmButton.disabled = !name || Boolean(error);
  }

  _recompose() {
    this.sheetCanvas = sheetToCanvas(composeAppearance({
      ...this.draft,
      name: repairName(this.nameInput.value),
    }));
  }

  _turn(step) {
    const index = DIRECTIONS.indexOf(this.direction);
    this.direction = DIRECTIONS[(index + step + DIRECTIONS.length) % DIRECTIONS.length];
  }

  _handleKey(event) {
    if (this.root.hidden) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      if (!this.deleteStrip.hidden) this._closeDeletePrompt();
      else this._cancel();
      return;
    }
    // Arrow keys move the caret while the name field has focus; only rotate
    // the preview when the keystroke lands outside a text input.
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this._turn(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this._turn(1);
    }
  }

  _tick(now) {
    if (this.root.hidden) return;
    this.walkTime = now / 1000;
    this._drawPreview();
    this.raf = requestAnimationFrame((time) => this._tick(time));
  }

  _drawPreview() {
    if (!this.sheetCanvas) return;
    const frame = walkFrame(this.walkTime, 4.375); // preview cycles at the player's stride
    const row = DIRECTIONS.indexOf(this.direction);
    const width = FRAME_WIDTH * PREVIEW_SCALE;
    const height = FRAME_HEIGHT * PREVIEW_SCALE;
    this.previewCtx.imageSmoothingEnabled = false;
    this.previewCtx.fillStyle = '#4f8c50';
    this.previewCtx.fillRect(0, 0, this.preview.width, this.preview.height);
    this.previewCtx.drawImage(
      this.sheetCanvas,
      frame * FRAME_WIDTH, row * FRAME_HEIGHT, FRAME_WIDTH, FRAME_HEIGHT,
      Math.round((this.preview.width - width) / 2),
      Math.round((this.preview.height - height) / 2),
      width, height,
    );
  }

  _confirm() {
    if (this.confirmButton.disabled) return;
    const appearance = repairAppearance({ ...this.draft, name: this.nameInput.value });
    appearance.name = repairName(this.nameInput.value);
    this.hide();
    this.onConfirm?.(appearance, this.mode, { ...this.npcDraft });
  }

  _cancel() {
    this.hide();
    this.onCancel?.(this.mode);
  }
}
